import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  MenuItem
} from '@mui/material';
import { Close as CloseIcon, PersonAdd as PersonAddIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import api from '../services/api';
import { REFERRAL_SEND_TEACHER_INVITE } from '../config/Config';
import AddStudentDialog from './AddStudentDialog';

const InviteDialog = ({ open, onClose }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [email, setEmail] = useState('');
  const [inviteType, setInviteType] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showAddStudentDialog, setShowAddStudentDialog] = useState(false);

  // Reset form state when dialog opens/closes
  useEffect(() => {
    if (!open) {
      // Reset all form state when dialog closes
      setEmail('');
      setInviteType('');
      setError('');
      setLoading(false);
      setShowAddStudentDialog(false);
    }
  }, [open]);

  const checkUserExists = async (email) => {
    try {
      const response = await api.post('/accounts/check-user-exists/', { email });
      return response.data;
    } catch (err) {
      console.error('Error checking user existence:', err);
      return null;
    }
  };

  const handleInviteTypeChange = (e) => {
    const value = e.target.value;
    setInviteType(value);
    
    if (value === 'my_student') {
      // Show Add Student Dialog instead of closing this one
      setShowAddStudentDialog(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Please enter an email address');
      return;
    }

    if (!inviteType) {
      setError('Please select an invite type');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // First check if user already exists
      const userCheck = await checkUserExists(email.trim());
      
      if (userCheck && userCheck.exists) {
        setError('User is already present in the system');
        setLoading(false);
        return;
      }

      // If user doesn't exist, proceed with sending invitation
      const response = await api.post(REFERRAL_SEND_TEACHER_INVITE, {
        email: email.trim()
      });

      if (response.data.existing_invite) {
        enqueueSnackbar('Invite resent successfully!', { variant: 'success' });
      } else {
        enqueueSnackbar('Invite sent successfully!', { variant: 'success' });
      }

      // Reset form and close dialog
      setEmail('');
      setInviteType('');
      onClose();
      
    } catch (err) {
      console.error('Error sending invite:', err);
      const errorMessage = err.response?.data?.error || 'Failed to send invite. Please try again.';
      setError(errorMessage);
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setEmail('');
      setInviteType('');
      setError('');
      onClose();
    }
  };

  const handleAddStudentSuccess = () => {
    // Show success message - AddStudentDialog will handle closing both dialogs
    enqueueSnackbar('Student added successfully!', { variant: 'success' });
  };

  const handleCloseAddStudentDialog = () => {
    setShowAddStudentDialog(false);
    // Reset the invite type selection when AddStudentDialog is closed
    setInviteType('');
  };

  // Custom close handler for AddStudentDialog that closes both dialogs
  const handleAddStudentDialogClose = () => {
    setShowAddStudentDialog(false);
    setInviteType('');
    // Also close the main invite dialog
    onClose();
  };

  return (
    <>
      <Dialog 
        open={open} 
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          m: 0, 
          p: 3, 
          pb: 2,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <PersonAddIcon color="primary" />
          <Typography variant="h6" component="span">
            Send Invitation
          </Typography>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            disabled={loading}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'text.secondary',
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ p: 3, pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Send an invitation to join ThinkStyne. The recipient will receive an email with a special invitation link.
            </Typography>

            <TextField
              select
              fullWidth
              label="Invite"
              value={inviteType}
              onChange={handleInviteTypeChange}
              required
              sx={{ mb: 2 }}
              InputProps={{
                sx: { borderRadius: 2 }
              }}
            >
              <MenuItem value="">
                <em>Select invite type</em>
              </MenuItem>
              <MenuItem value="my_student">My Student</MenuItem>
              <MenuItem value="others">Others</MenuItem>
            </TextField>

            {inviteType === 'others' && (
              <TextField
                autoFocus
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter email address"
                disabled={loading}
                error={!!error}
                sx={{ mb: 2 }}
                InputProps={{
                  sx: { borderRadius: 2 }
                }}
              />
            )}

            {inviteType === 'my_student' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Click "Add Student" below to open the student management dialog.
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button 
              onClick={handleClose} 
              disabled={loading}
              sx={{ borderRadius: 2 }}
            >
              Cancel
            </Button>
            {inviteType === 'others' && (
              <Button
                type="submit"
                variant="contained"
                disabled={loading || !email.trim()}
                startIcon={loading ? <CircularProgress size={16} /> : <PersonAddIcon />}
                sx={{ borderRadius: 2 }}
              >
                {loading ? 'Sending...' : 'Send Invitation'}
              </Button>
            )}

            {inviteType === 'my_student' && (
              <Button
                variant="contained"
                onClick={() => setShowAddStudentDialog(true)}
                startIcon={<PersonAddIcon />}
                sx={{ borderRadius: 2 }}
              >
                Add Student
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* Add Student Dialog */}
      <AddStudentDialog
        open={showAddStudentDialog}
        onClose={handleAddStudentDialogClose}
        onSuccess={handleAddStudentSuccess}
      />
    </>
  );
};

export default InviteDialog;
